// AddDatabaseModalFooter.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';

/**
 * AddDatabaseModalFooter
 *
 * Renders the step-specific footer actions for the add-database wizard.
 *
 * @param {object} flowState - Modal flow state that determines which actions are visible.
 * @param {object} dictionaryState - Template provisioning state and handlers.
 * @param {object} createTypeState - Create-type form state and handlers.
 * @param {object} modalActions - General modal navigation and close handlers.
 *
 * @returns {ReactNode} The rendered footer actions.
 */
const AddDatabaseModalFooter = ({
    flowState,
    dictionaryState,
    createTypeState,
    modalActions,
}) => {
    const { theme } = useTheme();

    if (flowState.isLandingStep) {
        return null;
    }

    return (
        <ModalButtons>

            {/* BACK NAVIGATION */}
            {(flowState.isTemplateStep || flowState.isCreateStep) && (
                <div className="add-database-modal-back">
                    <Button
                        backgroundColor="transparent"
                        hoverBackgroundColor={theme.buttonHoverBackgroundColor}
                        iconKey="arrow_back"
                        label="Back"
                        onClick={flowState.isTemplateStep ? dictionaryState.handleTemplateBack : modalActions.returnToLanding}
                    />
                </div>
            )}

            {flowState.isTemplateStep && dictionaryState.isVariantPreviewStep && (
                <Button
                    iconKey="check"
                    enabled={!dictionaryState.isProvisioning}
                    label={dictionaryState.isProvisioning ? 'Provisioning...' : 'Add to Workspace'}
                    onClick={dictionaryState.handleUseSelectedVariant}
                />
            )}

            {flowState.isCreateFlow && !flowState.hasCreatedType && (
                <Button
                    label={createTypeState.isSavingType ? 'Creating...' : 'Create Database'}
                    enabled={createTypeState.canSaveType}
                    onClick={createTypeState.handleCreateType}
                />
            )}

            {flowState.isCreateStep && flowState.hasCreatedType && (
                <>
                    <Button
                        label="Done"
                        onClick={modalActions.handleClose}
                    />
                    <Button
                        iconKey="check"
                        label={createTypeState.isUpdatingType ? 'Saving...' : 'Save Changes'}
                        enabled={createTypeState.canSaveCreatedType}
                        onClick={createTypeState.handleSaveCreatedType}
                    />
                </>
            )}

            {flowState.isFieldsStep && (
                <Button
                    label="Done"
                    onClick={modalActions.handleClose}
                />
            )}

        </ModalButtons>
    );
};

export default AddDatabaseModalFooter;